import { cloneElement, Fragment, createElement as rc, useCallback, useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import View from './View';
import Text from './Text';
import PropTypes from 'prop-types';
import styled from '../styled';
import webOnlyStyles from '../webOnlyStyles';
import fromTheme from '../fromTheme';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'styled-components';
import FeedbackIcon from './Icons/FeedbackIcon.gl2025';
import { useBbState } from '../hooks';
import constants from 'lib_ui-services/src/constants';

let Anchor = styled(View).attrs({ name: 'TooltipAnchor' })`
    display: inline-block;
    margin: ${fromTheme('tooltip', 'anchor', 'margin')};
`;
Anchor = webOnlyStyles(Anchor)`
    cursor: help;
`;
Anchor.displayName = 'TooltipAnchor';

let TooltipTitle = styled(Text)`
    ${fromTheme('tooltip', 'titleFont')};
`;
TooltipTitle.displayName = 'TooltipTitle';

let TooltipBody = styled(Text)`
    ${fromTheme('tooltip', 'bodyFont')};
    margin-top: 4px;
`;
TooltipBody.displayName = 'TooltipBody';

let _Tooltip = styled(View).attrs({ name: 'Tooltip' })``;
_Tooltip.displayName = 'Tooltip';

let TooltipContainer = styled(ReactTooltip)`
    position: absolute;
    background-color: ${fromTheme('tooltip', 'backgroundColor')} !important;
    padding: ${fromTheme('tooltip', 'padding')} !important;
    border-radius: ${fromTheme('tooltip', 'radius')} !important;
    color: ${fromTheme('tooltip', 'color')} !important;
    z-index: ${fromTheme('tooltip', 'zIndex')} !important;
    max-width: ${fromTheme('tooltip', 'maxWidth')} !important;
`;
TooltipContainer = webOnlyStyles(TooltipContainer)`
    box-shadow: ${fromTheme('tooltip', 'boxShadow')} !important;
`;
TooltipContainer.displayName = 'TooltipContainer';

const TopToBottom = styled(View)`
    flex-direction: column;
    max-width: ${fromTheme('tooltip', 'maxWidth')} !important;
`;

/**
 * tooltip based on react-tooltip
 * @see https://react-tooltip.com/docs/getting-started
 * @param {object} param
 * @returns
 */

const Tooltip = ({ title, body, children, id = title, defaultOpen = false }) => {
    const [wasOpened, setWasOpened, ready] = useBbState(false, `wasOpened${id}`, {
        scope: constants.retention.SCOPE.GLOBAL,
        visibility: constants.retention.VISIBILITY.PER_BROWSER,
        duration: constants.retention.DURATION.ALWAYS
    });

    children = useMemo(() => {
        const childArray = children && !Array.isArray(children) ? [children] : children;

        return childArray
            ? childArray.map(child => {
                  if (child?.props?.hNode) {
                      return cloneElement(child, {
                          ...child.props,
                          hNode: { ...child.props.hNode, badge: wasOpened || !ready ? 'none' : 'info' }
                      });
                  }
                  return child;
              })
            : rc(FeedbackIcon, {
                  name: 'info',
                  variant: 'secondary',
                  color: 'primary',
                  size: 'xs'
              });
    }, [children, wasOpened, ready]);

    const [header, ...content] = useMemo(() => {
        // if there is no body, and the title contains newlines or `<br\>`, split it into multiple lines
        if (!body) {
            return title.split(/[\n\r]+|(\\n)|<br\s*\\?>/gi);
        }

        return Array.isArray(body) ? [title, ...body] : [title, body];
    }, [title, body]);

    const theme = useTheme();

    // we need to pass in the theme manually, as these elements are rendered outside the react tree
    // and otherwise they would not have access to the theme
    const displayedContent = useMemo(
        () =>
            ReactDOMServer.renderToStaticMarkup(
                // prettier-ignore
                rc(TopToBottom, { theme },
                    header && rc(TooltipTitle, { theme }, header),
                    content && content.map((line, index) => rc(TooltipBody, { key: index, theme }, line))
                )
            ),
        [header, content, theme]
    );

    const markAsVisited = useCallback(() => {
        if (ready && !wasOpened) {
            setWasOpened(true);
        }
    }, [ready, wasOpened, setWasOpened]);

    if (theme.mobile || !title) {
        return rc(View, null, children);
    }

    // prettier-ignore
    return rc(_Tooltip, null,
        rc(Anchor,
            {
                id,
                // this is what targets one tooltip portal or the other. it is NOT the id of the tooltip itself
                'data-tooltip-id': ready && defaultOpen && !wasOpened ?'TooltipPopupDefaultOpen' : 'TooltipPopup' ,
                'data-tooltip-place': 'top',
                'data-tooltip-html': displayedContent,
                onClick:markAsVisited,
                onMouseLeave:markAsVisited
            },
            children
        )
    );
};

Tooltip.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    defaultOpen: PropTypes.bool,
    children: PropTypes.node
};

Tooltip.Popup = () =>
    rc(
        Fragment,
        null,
        rc(TooltipContainer, { id: 'TooltipPopupDefaultOpen', clickable: true, defaultIsOpen: true, disableStyleInjection: 'core' }),
        rc(TooltipContainer, { id: 'TooltipPopup', clickable: true, disableStyleInjection: 'core' })
    );

export default Tooltip;
